/* =========================================================
   Cambio obligatorio de contraseña.

   Se muestra en lugar de la aplicación cuando la cuenta entró con una
   contraseña temporal — las que reparte el alta masiva. No hay forma
   de saltárselo salvo cerrando sesión: mientras la contraseña temporal
   siga puesta, cualquiera con el CSV puede entrar.
   ========================================================= */
import React from 'react';
import { ic, sized } from '@/components/Icons';
import { Logo, PasswordInput } from '@/components/ui';
import { useI18n } from '@/i18n';
import { changePassword } from '@/lib/api';
import { PasswordStrength, passwordScore } from './parts';

interface Props {
  /** El nombre de quien entró, para que se vea que la sesión ya es suya. */
  name: string;
  /** El correo de la cuenta: no se edita, pero el formulario lo necesita
   *  para que un gestor de contraseñas sepa a quién asociar la nueva. */
  email: string;
  onDone: () => void;
  onCancel: () => void;
}

export function ForcePasswordChange({ name, email, onDone, onCancel }: Props) {
  const { t } = useI18n();
  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [repeat, setRepeat] = React.useState('');
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const corta = next.length > 0 && next.length < 12;
  const noCoincide = repeat.length > 0 && next !== repeat;
  const listo = current.length > 0 && next.length >= 12 && next === repeat && !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!listo) return;
    setBusy(true);
    setErr('');
    try {
      const res = await changePassword(current, next);
      if (!res.ok) { setErr(res.message); return; }
      onDone();
    } finally {
      setBusy(false);
    }
  }

  const campo = (
    label: string,
    value: string,
    set: (v: string) => void,
    autoComplete: string,
    invalid?: boolean,
  ) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>{label}</span>
      <PasswordInput
        className="input"
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => set(e.target.value)}
        aria-invalid={invalid || undefined}
        style={invalid ? { borderColor: 'var(--err)' } : undefined}
      />
    </label>
  );

  return (
    <div
      className="screen-enter"
      style={{
        minHeight: '100vh', background: 'var(--bg)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: '100%', maxWidth: 420, background: 'var(--surface)',
          border: '1px solid var(--line)', borderRadius: 14, boxShadow: 'var(--shadow-3)',
          padding: 28, display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <Logo size={20} />

        <div>
          <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 600, letterSpacing: '-0.012em' }}>
            {t('chpw.title')}
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
            {t('chpw.intro', { name })}
          </p>
        </div>

        {/* Sin un campo de usuario, el navegador no sabe a qué cuenta
            pertenece la contraseña nueva y no se ofrece a guardarla. */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={email}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        />

        {campo(t('chpw.current'), current, setCurrent, 'current-password')}
        {campo(t('chpw.next'), next, setNext, 'new-password', corta)}
        <PasswordStrength p={next} />
        {campo(t('chpw.repeat'), repeat, setRepeat, 'new-password', noCoincide)}

        {corta ? (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--err)' }}>{t('chpw.too_short')}</p>
        ) : null}
        {noCoincide ? (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--err)' }}>{t('chpw.mismatch')}</p>
        ) : null}
        {err ? (
          <div
            role="alert"
            style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13,
              background: 'var(--st-req-l)', color: 'var(--st-req)',
              padding: '10px 12px', borderRadius: 8,
            }}
          >
            {sized(ic.warn, 15)} <span>{err}</span>
          </div>
        ) : null}

        <button
          type="submit"
          className="btn primary"
          disabled={!listo}
          style={{ justifyContent: 'center', padding: '11px 14px', marginTop: 4 }}
        >
          {busy ? t('chpw.saving') : t('chpw.submit')}
        </button>

        {/* No hay «más tarde»: la salida es cerrar sesión. */}
        <button
          type="button"
          className="btn ghost"
          onClick={onCancel}
          style={{ justifyContent: 'center', fontSize: 12, color: 'var(--text-2)' }}
        >
          {t('chpw.signout')}
        </button>

        {/* Solo se avisa cuando la elegida es floja de verdad; si no, es ruido. */}
        {next.length >= 12 && passwordScore(next) < 2 ? (
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text-3)', textAlign: 'center' }}>
            {t('chpw.weak_hint')}
          </p>
        ) : null}
      </form>
    </div>
  );
}
